// frontend/src/pages/admin/AdminContent.js
import React, { useState, useEffect } from 'react';
import { settingsApi } from '../../api/settings';
import { aiApi } from '../../api/ai';
import { FaPenNib, FaSave, FaSpinner, FaInfoCircle, FaExclamationTriangle, FaRobot, FaMagic, FaExternalLinkAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const DEFAULTS = {
    about_us: "Everything Automotive is your trusted partner...",
    terms_of_service: "Terms of Service...",
    privacy_policy: "Privacy Policy...",
    partner_agreement: "Partner Agreement..."
};

// Map internal keys to public frontend routes
const LIVE_URLS = {
    about_us: '/about',
    terms_of_service: '/terms',
    privacy_policy: '/privacy-policy',
    partner_agreement: '/partner-terms'
};

function AdminContent() {
  const [activeTab, setActiveTab] = useState('about_us');
  const [content, setContent] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // AI State
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await settingsApi.getAllSettings();
      const pagesData = data.pages_content || {};
      
      setContent({
        about_us: pagesData.about_us || DEFAULTS.about_us,
        terms_of_service: pagesData.terms_of_service || DEFAULTS.terms_of_service,
        privacy_policy: pagesData.privacy_policy || DEFAULTS.privacy_policy,
        partner_agreement: pagesData.partner_agreement || DEFAULTS.partner_agreement
      });
    } catch (err) {
      console.error("Failed to load content:", err);
      setError("Connection failed. Loaded default templates.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setContent(prev => ({ ...prev, [activeTab]: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.updateSettings('pages_content', content);
      alert("Content updated successfully!");
      setError(null); 
    } catch (err) {
      console.error(err);
      alert("Failed to save content. Please check your connection.");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateAI = async (e) => {
      e.preventDefault();
      if (!aiPrompt.trim()) return alert("Please enter a prompt for the AI.");

      console.log(">>> [DEBUG FRONTEND] Sending AI Request:", { prompt: aiPrompt, pageType: activeTab });
      setIsGenerating(true);
      
      try {
          const response = await aiApi.generateCMSContent(aiPrompt, activeTab);
          console.log(">>> [DEBUG FRONTEND] AI Response:", response);
          
          if (response.content) {
              setContent(prev => ({ ...prev, [activeTab]: response.content }));
              setAiPrompt(''); // Clear prompt on success
          } else {
              console.warn(">>> [DEBUG FRONTEND] Response missing 'content' field.");
              alert("AI generated empty content.");
          }
      } catch (err) {
          console.error(">>> [DEBUG FRONTEND] AI Error:", err);
          alert("AI Generation failed: " + (err.message || "Unknown error"));
      } finally {
          setIsGenerating(false);
      }
  };

  if (loading) return <div className="p-8 text-center"><FaSpinner className="animate-spin text-2xl text-primary" /> Loading CMS...</div>;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
        <FaPenNib className="text-primary" /> Content Management System (CMS)
      </h1>

      {error && (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4 flex items-center gap-2">
          <FaExclamationTriangle /> {error}
          <button onClick={fetchData} className="ml-auto text-sm underline hover:text-yellow-900">Retry</button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        
        {/* TABS */}
        <div className="flex border-b border-gray-200 bg-gray-50 overflow-x-auto">
          {['about_us', 'terms_of_service', 'privacy_policy', 'partner_agreement'].map(tab => (
              <button 
                key={tab}
                onClick={() => { setActiveTab(tab); setAiPrompt(''); }}
                className={`px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap capitalize ${activeTab === tab ? 'bg-white border-t-2 border-primary text-primary' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                {tab.replace(/_/g, ' ')}
              </button>
          ))}
        </div>

        {/* EDITOR AREA */}
        <div className="p-6">
          <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-blue-50 p-3 rounded border border-blue-100">
            <div className="flex items-start gap-2">
                <FaInfoCircle className="text-blue-500 mt-1 flex-shrink-0" />
                <p className="text-sm text-blue-700">
                You are editing the <strong>{activeTab.replace(/_/g, ' ').toUpperCase()}</strong> page.
                </p>
            </div>
            
            {/* VIEW LIVE PAGE BUTTON */}
            <Link 
                to={LIVE_URLS[activeTab]} 
                target="_blank" 
                className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 bg-white px-3 py-1.5 rounded border border-blue-200 shadow-sm hover:shadow transition-all"
            >
                <FaExternalLinkAlt /> View Live Page
            </Link>
          </div>

          <textarea
            value={content[activeTab]}
            onChange={handleChange}
            className="w-full h-96 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:outline-none font-mono text-sm"
            placeholder={`Enter content for ${activeTab}...`}
          ></textarea>

          <div className="mt-6 flex justify-end">
            <button 
              onClick={handleSave}
              disabled={saving}
              className="bg-primary hover:bg-primary-dark text-white font-bold py-3 px-8 rounded shadow-md flex items-center gap-2"
            >
              {saving ? <FaSpinner className="animate-spin" /> : <FaSave />} Save Content
            </button>
          </div>
        </div>

        {/* AI GENERATOR SECTION */}
        <div className="border-t border-gray-200 p-6 bg-gray-50">
            <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                <FaRobot className="text-purple-600" /> AI Content Generator
            </h3>
            <p className="text-sm text-gray-600 mb-4">
                Describe what you want for the <strong>{activeTab.replace(/_/g, ' ')}</strong> page, and the AI will write it for you.
            </p>
            
            <form onSubmit={handleGenerateAI} className="flex gap-3">
                <input 
                    type="text" 
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder={`e.g. Write a professional ${activeTab.replace(/_/g, ' ')} for a Nigerian auto parts store...`}
                    className="flex-grow p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none"
                    disabled={isGenerating}
                />
                <button 
                    type="submit" 
                    disabled={isGenerating || !aiPrompt.trim()}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg shadow-md flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isGenerating ? <FaSpinner className="animate-spin" /> : <FaMagic />} Generate
                </button>
            </form>
            <p className="text-xs text-gray-500 mt-2">
                * Generated content will replace the text in the editor above. You can edit it before saving.
            </p>
        </div>

      </div>
    </div>
  );
}

export default AdminContent;